import { DateTime } from 'luxon';
import { JourneyHelper } from './journey.helper';
import { IdHelper } from './id.helper';
import type { IJourney } from '../models/journey.model';
import { TranslationService } from '../services/translation.service';
import { DateHelper } from './date.helper';
import { StopAreaHelper } from './stop-area.helper';
import type { ILegBase } from '../models/leg-base.model';
import { LegHelper } from './leg.helper';
import type { ITripLeg } from '../models/trip-leg.model';
import type { IConnectionLink } from '../models/connection-link.model';
import { TransportMode } from '../models/transport-mode.enum';
import { NoteHelper } from './note.helper';
import { DistanceHelper } from './distance.helper';

export class CalendarHelper {
  private static calendarDateFormat = "yyyyMMdd'T'HHmmss'Z'";
  public static GenerateGoogleCalendarLink(journey: IJourney): string {
    const departureTime = JourneyHelper.getDepartureTimeForJourney(journey, false);
    const arrivalTime = JourneyHelper.getArrivalTimeForJourney(journey, false);

    const text = this.getCalendarSummary(journey);
    const details = this.getCalendarDescription(journey, this.lineBreakUrl);
    const date1 = DateTime.fromISO(departureTime).toUTC().toFormat(this.calendarDateFormat);
    const date2 = DateTime.fromISO(arrivalTime).toUTC().toFormat(this.calendarDateFormat);

    return `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(text)}&details=${encodeURIComponent(details)}&dates=${date1}/${date2}`;
  }

  public static CreateIcsFile(journey: IJourney): string {
    const departureTime = JourneyHelper.getDepartureTimeForJourney(journey, false);
    const arrivalTime = JourneyHelper.getArrivalTimeForJourney(journey, false);

    const date1 = DateTime.fromISO(departureTime).toUTC().toFormat(this.calendarDateFormat);
    const date2 = DateTime.fromISO(arrivalTime).toUTC().toFormat(this.calendarDateFormat);

    const ics =
      'BEGIN:VCALENDAR' +
      this.lnLf +
      'VERSION:2.0' +
      this.lnLf +
      'PRODID://Västtrafik AB//www.vasttrafik.se//SV' +
      this.lnLf +
      'METHOD:PUBLISH' +
      this.lnLf +
      'BEGIN:VEVENT' +
      this.lnLf +
      `UID:${IdHelper.newGuid()}` +
      this.lnLf +
      'CLASS:PUBLIC' +
      this.lnLf +
      `SUMMARY:${this.getCalendarSummary(journey)}` +
      this.lnLf +
      'LOCATION:' +
      this.lnLf +
      `DTSTART:${date1}` +
      this.lnLf +
      `DTEND:${date2}` +
      this.lnLf +
      `DTSTAMP:${DateTime.now().toUTC().toFormat(this.calendarDateFormat)}` +
      this.lnLf +
      `DESCRIPTION:${this.getCalendarDescription(journey, this.lf)}` +
      this.lnLf +
      'END:VEVENT' +
      this.lnLf +
      'END:VCALENDAR';

    return ics;
  }

  private static lf = '\\n';
  private static lnLf = '\r\n';
  private static lineBreakUrl = '\n';

  private static getCalendarSummary(journey: IJourney): string {
    const firstTripLeg = JourneyHelper.getFirstPublicTransportLeg(journey);
    const lastTripLeg = JourneyHelper.getLastPublicTransportLeg(journey);
    const fromName = firstTripLeg?.origin.stopPoint.name ?? '?';
    const toName = lastTripLeg?.destination.stopPoint.name ?? '?';

    return `${TranslationService.get('travelplanner.mytrip')}: ${fromName} ${TranslationService.get('travelplanner.to').toLowerCase()} ${toName}`;
  }

  private static getCalendarDescription(journey: IJourney, lineBreak: string): string {
    const numberOfInterchanges = JourneyHelper.getNumberOfInterchanges(journey);
    const durationInMinutes = JourneyHelper.getDurationInMinutes(journey, false);
    const durationTime = DateHelper.formatAsHoursAndMinutes(durationInMinutes);

    let description = TranslationService.get('travelplanner.tripinformation') + lineBreak + lineBreak;
    if (numberOfInterchanges > 0) {
      description += `${TranslationService.get('common.interchanges')}: ${numberOfInterchanges}${lineBreak}`;
    }
    description += `${TranslationService.get('common.traveltime')}: ${durationTime}${lineBreak}${lineBreak}`;

    if (journey.departureAccessLink) {
      description += this.getAccessLinkDescription(
        journey.departureAccessLink.distanceInMeters,
        `${StopAreaHelper.getNameWithoutMunicipality(journey.departureAccessLink.destination.name)} ${this.getTrackTranslated(journey.departureAccessLink.destination.platform)}`,
        journey.departureAccessLink.transportMode,
        lineBreak
      );
    }

    journey.allLegs?.forEach((leg) => {
      description += this.getLegDescription(leg, lineBreak);
    });

    if (journey.arrivalAccessLink) {
      description += this.getAccessLinkDescription(
        journey.arrivalAccessLink.distanceInMeters,
        StopAreaHelper.getNameWithoutMunicipality(journey.arrivalAccessLink.destination.name),
        journey.arrivalAccessLink.transportMode,
        lineBreak
      );
    }

    description += TranslationService.get('travelplanner.calendardisclaimer');

    return description;
  }

  private static getAccessLinkDescription(distanceInMeters: number | undefined, destinationName: string, transportMode: TransportMode, lineBreak: string): string {
    const description = `${this.getActivityName(transportMode)} ${DistanceHelper.format(distanceInMeters)} ${TranslationService.get('travelplanner.to').toLowerCase()} ${destinationName}${
      lineBreak + lineBreak
    }`;
    return description;
  }

  private static getLegDescription(leg: ILegBase, lineBreak: string): string {
    let description = '';
    const isTripLeg = LegHelper.isTripLeg(leg);

    if (isTripLeg && (leg as ITripLeg).plannedConnectingTimeInMinutes !== undefined) {
      const tripLeg = leg as ITripLeg;
      if (tripLeg.isRiskOfMissingConnection) {
        description += TranslationService.get('common.interchange.riskofmissingconnection');
      } else {
        const durationInMinutes = tripLeg.plannedConnectingTimeInMinutes;
        description += `${TranslationService.get('common.interchange.time')} ${DateHelper.formatAsHoursAndMinutes(durationInMinutes)}`;
      }
      description += lineBreak + lineBreak;
    }
    if (!isTripLeg) {
      const connectionLink = leg as IConnectionLink;

      const notes = this.getNotesFromTripLegOriginAndDestination(connectionLink);
      if (notes && notes.length > 0) {
        description += notes.join(lineBreak) + lineBreak;
      }

      description += `${this.getActivityName(connectionLink.transportMode)} ${DistanceHelper.format(connectionLink.distanceInMeters)} ${TranslationService.get(
        'travelplanner.to'
      ).toLowerCase()} ${StopAreaHelper.getNameWithoutMunicipality(connectionLink.destination.stopPoint.name)} ${this.getTrackTranslated(
        connectionLink.destination.stopPoint.platform
      )}${lineBreak}${lineBreak}`;
    } else {
      const tripLeg = leg as ITripLeg;
      description += tripLeg.serviceJourney.line?.name ?? '';
      description += tripLeg.serviceJourney.direction ? ` ${TranslationService.get('common.towards').toLowerCase()} ${tripLeg.serviceJourney.direction}` : '';
      description += lineBreak;

      const notes = this.getNotesFromTripLegOriginAndDestination(tripLeg);
      if (notes && notes.length > 0) {
        description += notes.join(lineBreak) + lineBreak;
      }

      description += `${DateHelper.getTimeFromDateTimeString(LegHelper.getDepartureTime(tripLeg, false))} ${StopAreaHelper.getNameWithoutMunicipality(
        tripLeg.origin.stopPoint.name
      )} ${this.getTrackTranslated(tripLeg.origin.stopPoint.platform)}${lineBreak}`;

      description += `${DateHelper.getTimeFromDateTimeString(LegHelper.getArrivalTime(tripLeg, false))} ${StopAreaHelper.getNameWithoutMunicipality(
        tripLeg.destination.stopPoint.name
      )} ${this.getTrackTranslated(tripLeg.destination.stopPoint.platform)}${lineBreak + lineBreak}`;
    }

    return description;
  }

  private static getNotesFromTripLegOriginAndDestination(leg: ILegBase): string[] {
    return NoteHelper.removeDuplicateNotesAndSelectOnlyText(LegHelper.getAllNotes(leg));
  }

  // private static getDuration(originTime: string, destinationTime: string): number {
  //   return DateHelper.getDurationFromDateStrings(originTime, destinationTime);
  // }

  private static getTrackTranslated(platform?: string): string {
    if (!platform) {
      return '';
    }
    return `${TranslationService.get('common.platform')} ${platform}`;
  }

  private static getActivityName(transportMode: TransportMode): string {
    if (transportMode === TransportMode.walk) {
      return TranslationService.get('common.activity.walk');
    }
    if (transportMode === TransportMode.bike) {
      return TranslationService.get('common.activity.bike');
    }
    if (transportMode === TransportMode.car) {
      return TranslationService.get('common.activity.drive');
    }
    return '';
  }
}
